import { RADIUS, WithHalfdot } from "@elyxeer/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { KEYBOARD_INSET_VAR } from "~/lib/keyboard-inset";
import { useAuthStore } from "~/store/auth-store";

import { type CodeStepState, translateAuthError } from "./auth-flow";
import { CodeField } from "./CodeField";

/**
 * Step two of passwordless admin sign-in: a dedicated screen for the code that arrived
 * by e-mail. With the keypad open, the input and primary action still own the full
 * available surface instead of competing with the introductory sign-in content.
 */
export function VerifyScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: CodeStepState | null };
  const verify = useAuthStore((store) => store.verify);
  const config = useAuthStore((store) => store.config);

  const [code, setCode] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codeLength = config?.code_length ?? 6;
  const email = state?.email ?? "";

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrorKey(null);
    try {
      await verify(email, code);
      // The authenticated gate hides this route, but it becomes active again after
      // sign-out. Replace it now so a non-admin member returns to the e-mail form
      // instead of seeing the already-consumed verification step.
      navigate("/", { replace: true });
    } catch (error) {
      setErrorKey(translateAuthError(error));
    } finally {
      setBusy(false);
    }
  };

  // A reload drops router state. A code without the request that produced it cannot be
  // verified, so restart instead of leaving an unusable form on screen.
  if (!email) return <Navigate to="/auth" replace />;

  return (
    <Box
      component="form"
      onSubmit={submitCode}
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        paddingInline: 3,
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        paddingBottom: `calc(24px + env(safe-area-inset-bottom) + var(${KEYBOARD_INSET_VAR}, 0px))`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { md: 460 },
          marginInline: { md: "auto" },
          marginBlock: { md: "auto" },
          flex: { xs: 1, md: "0 1 auto" },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            aria-label={t("common.back")}
            onClick={() => navigate(-1)}
            disabled={busy}
            data-testid="auth.back"
            sx={{ color: "text.secondary", marginLeft: -1, flexShrink: 0 }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Development only. Keeping it in the header preserves the production
              layout when OTP echo is enabled locally. */}
          {state?.debugCode ? (
            <Box
              data-testid="auth.debug-code"
              sx={{
                flex: 1,
                minWidth: 0,
                paddingInline: 1.5,
                paddingBlock: 0.5,
                borderRadius: `${RADIUS.full}px`,
                backgroundColor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography
                variant="L3"
                sx={{ color: "text.muted", display: "block" }}
              >
                {t("auth.devCodeHint", { code: state.debugCode })}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={12}>{t("auth.codeTitle")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1.5 }}
          >
            {t("auth.codeSubtitle", {
              email,
              minutes: Math.max(1, Math.round((state?.expiresIn ?? 600) / 60)),
            })}
          </Typography>
        </Box>

        <CodeField
          value={code}
          onChange={setCode}
          length={codeLength}
          label={t("auth.codeLabel")}
          error={errorKey !== null}
          autoFocus
          testId="auth.code"
        />

        {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}

        <Box sx={{ marginTop: "auto" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={busy || code.length < codeLength}
            data-testid="auth.verify"
          >
            {t("common.continue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
